/**
 * Merge helpers for the Supabase refetch path.
 *
 * `useSyncWithRetry` re-runs `fetchCurrentGame` on mount, gameId change,
 * visibilitychange, window focus, pageshow and online. Its response is a
 * SNAPSHOT, and it used to be written into the store with a wholesale
 * assignment — so a response taken before a socket event that landed while it
 * was in flight would REWIND state that the socket had already advanced.
 *
 * The audible symptom was phase voice-overs replaying: `race_starting` would
 * disappear from a round, the latest event would fall back to `race_ready`, and
 * "place your bets" would sound again seconds after betting had closed. The
 * read-replica lag documented in [[movesPointerForward]] widens the window from
 * a few hundred ms to whole rounds, but no lag is needed — only that the
 * response is older than a socket event.
 *
 * The rule these encode: a fetch may ADD to what we know, never SHRINK it.
 */

/**
 * Union of a fetched id list with what the store already had.
 *
 * Order is not significant for the caller that uses this (race EVENT ids are
 * re-sorted by timestamp when the selector reads them), so incoming order is
 * kept and previously-known extras are appended.
 */
export const unionFetchedIds = (
  incoming: readonly string[],
  existing: readonly string[] | undefined
): string[] => {
  if (!existing || existing.length === 0) return [...incoming]

  const seen = new Set(incoming)

  return [...incoming, ...existing.filter((id) => !seen.has(id))]
}

/**
 * How many rounds to retain per game after a merge.
 *
 * Matches the fetch's own `limit`, so a long session cannot grow the list
 * without bound just because every refetch unions into it.
 */
export const MERGED_RACE_LIMIT = 10

/**
 * Union of fetched race ids with the known ones, ordered NEWEST FIRST.
 *
 * Unlike event ids, race id ORDER is semantic: the selector maps `game.raceIds`
 * in stored order and the rest of the app treats `races[0]` as the current
 * round. So a socket-fresh round missing from a lagging snapshot must come back
 * at the FRONT, not merely be retained — otherwise the pointer rolls backwards
 * onto the previous, settled round and rolls forward again on the next event,
 * announcing a phase twice per refocus.
 *
 * Races with no known `createdAt` sort last: they are the ones we cannot place,
 * and guessing them into the front would be the very bug this prevents.
 */
export const mergeRaceIdsByRecency = (
  incoming: readonly string[],
  existing: readonly string[] | undefined,
  createdAtOf: (id: string) => string | undefined,
  limit: number = MERGED_RACE_LIMIT
): string[] => {
  const merged = unionFetchedIds(incoming, existing)

  return merged
    .map((id, index) => ({ id, index, createdAt: createdAtOf(id) }))
    .sort((a, b) => {
      if (a.createdAt === b.createdAt) return a.index - b.index
      if (!a.createdAt) return 1
      if (!b.createdAt) return -1

      return a.createdAt < b.createdAt ? 1 : -1
    })
    .slice(0, limit)
    .map((entry) => entry.id)
}
